import { Flex, Popover } from "antd";
import classNames from "classnames";
import { ToastService } from "@shared/lib/services";
import {
  CalendarDays,
  Check,
  CheckSquare,
  ChevronDown,
  Loader2,
  Pencil,
  RotateCcw,
  Square,
  X,
} from "lucide-react";
import React, { useState } from "react";
import styles from "../styles.module.scss";
import { RangePreset } from "../types";
import { formatRangeShort, toISODate } from "../utils/dateUtils";

interface GanttHeaderProps {
  rangeStart: Date;
  rangeEnd: Date;
  rangePresets: RangePreset[];
  editMode: boolean;
  isSaving?: boolean;
  someCreatorsSelected: boolean;
  allCreatorsSelected: boolean;
  selectedCount: number;
  totalCreatorsCount: number;
  onToggleEditMode: () => void;
  onCancelEditMode?: () => void;
  onSelectAllCreators: () => void;
  onClearCreatorSelection: () => void;
  onRangeStartChange: (val: string) => void;
  onRangeEndChange: (val: string) => void;
  onApplyPreset: (preset: RangePreset) => void;
  onApplyStageDurations: (durations: Record<string, number>) => void;
}

const STAGE_DURATION_ITEMS = [
  { key: "brief", label: "Brief", dotClass: styles.durationDotBrief },
  { key: "script", label: "Script", dotClass: styles.durationDotScript },
  { key: "draft", label: "Draft", dotClass: styles.durationDotDraft },
  { key: "live", label: "Live", dotClass: styles.durationDotLive },
] as const;

const LEGEND_ITEMS = [
  { kind: "done", label: "Done", colorClass: styles.barDone },
  { kind: "active", label: "In progress", colorClass: styles.barActive },
  { kind: "blocked", label: "Needs changes", colorClass: styles.barBlocked },
  { kind: "upcoming", label: "Upcoming", colorClass: styles.barUpcoming },
  { kind: "cancelled", label: "Cancelled", colorClass: styles.barCancelled },
] as const;

export const GanttHeader: React.FC<GanttHeaderProps> = ({
  rangeStart,
  rangeEnd,
  rangePresets,
  editMode,
  isSaving = false,
  someCreatorsSelected,
  allCreatorsSelected,
  selectedCount,
  totalCreatorsCount,
  onToggleEditMode,
  onCancelEditMode,
  onSelectAllCreators,
  onClearCreatorSelection,
  onRangeStartChange,
  onRangeEndChange,
  onApplyPreset,
  onApplyStageDurations,
}) => {
  const [schedulePopoverOpen, setSchedulePopoverOpen] = useState(false);
  const [stageDurations, setStageDurations] = useState<Record<string, number>>({
    brief: 5,
    script: 7,
    draft: 7,
    live: 5,
  });

  const isDateRangeInvalid = rangeEnd < rangeStart;

  const totalStageDays =
    (stageDurations.brief || 0) +
    (stageDurations.script || 0) +
    (stageDurations.draft || 0) +
    (stageDurations.live || 0);

  const handleDurationChange = (key: string, value: number) => {
    setStageDurations((prev) => ({
      ...prev,
      [key]: Math.max(1, value),
    }));
  };

  const handleApplyDurations = () => {
    if (onApplyStageDurations) {
      onApplyStageDurations(stageDurations);
    }
    setSchedulePopoverOpen(false);
  };

  const scheduleContent = (
    <div className={styles.schedulePopoverContent}>
      <div className={styles.scheduleSection}>
        <div className={styles.scheduleSectionTitle}>Quick Range</div>
        <div className={styles.presetsGrid}>
          {rangePresets.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={styles.presetButton}
              onClick={() => {
                onApplyPreset(preset);
                setSchedulePopoverOpen(false);
              }}
            >
              <span className={styles.presetLabel}>{preset.label}</span>
              <span className={styles.presetHint}>{preset.hint}</span>
            </button>
          ))}
        </div>
      </div>

      <div className={styles.scheduleSection}>
        <div className={styles.scheduleSectionTitle}>Custom Dates</div>
        <Flex align="center" gap={8}>
          <div className={styles.dateInputWrapper}>
            <span className={styles.dateInputLabel}>Start</span>
            <input
              type="date"
              className={classNames(styles.dateNativeInput, {
                [styles.dateInputError]: isDateRangeInvalid,
              })}
              value={toISODate(rangeStart)}
              onChange={(e) => onRangeStartChange(e.target.value)}
            />
          </div>
          <span className={styles.dateSeparator}>→</span>
          <div className={styles.dateInputWrapper}>
            <span className={styles.dateInputLabel}>End</span>
            <input
              type="date"
              className={classNames(styles.dateNativeInput, {
                [styles.dateInputError]: isDateRangeInvalid,
              })}
              value={toISODate(rangeEnd)}
              onChange={(e) => onRangeEndChange(e.target.value)}
            />
          </div>
        </Flex>
        {isDateRangeInvalid && (
          <div className={styles.dateErrorText}>
            End date cannot be less than start date
          </div>
        )}
      </div>

      <div className={styles.scheduleSection}>
        <div className={styles.scheduleSectionTitle}>Stage Durations</div>
        <div className={styles.durationsGrid}>
          {STAGE_DURATION_ITEMS.map((item) => (
            <div key={item.key} className={styles.durationCard}>
              <div className={styles.durationCardHeader}>
                <span className={classNames(styles.durationDot, item.dotClass)} />
                <span className={styles.durationStageLabel}>{item.label}</span>
              </div>
              <div className={styles.durationInputWrapper}>
                <input
                  type="number"
                  min={1}
                  max={365}
                  className={styles.durationInput}
                  value={stageDurations[item.key] ?? ""}
                  onChange={(e) => {
                    const parsed = parseInt(e.target.value, 10);
                    handleDurationChange(item.key, isNaN(parsed) ? 1 : parsed);
                  }}
                />
                <span className={styles.durationUnit}>days</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        className={styles.applyDurationsBtn}
        onClick={handleApplyDurations}
      >
        <CalendarDays size={13} />
        <span>Apply stage durations</span>
        <span className={styles.totalDurationBadge}>{totalStageDays}d total</span>
      </button>
    </div>
  );

  return (
    <div className={styles.headerToolbar}>
      <Flex align="center" justify="space-between" wrap="wrap" gap={12} style={{ width: "100%" }}>
        <Flex align="center" gap={12} wrap="wrap">
          <Popover
            content={scheduleContent}
            overlayClassName={styles.schedulePopoverOverlay}
            title={
              <div className={styles.popoverHeader}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span className={styles.popoverTitle}>Schedule settings</span>
                  <span className={styles.popoverSubtitle}>
                    Set the chart window. Stages fill it evenly until you edit dates.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setSchedulePopoverOpen(false)}
                  className={styles.popoverCloseBtn}
                  aria-label="Close schedule settings"
                >
                  <X size={16} />
                </button>
              </div>
            }
            trigger="click"
            open={schedulePopoverOpen}
            onOpenChange={setSchedulePopoverOpen}
            placement="bottomLeft"
          >
            <button
              type="button"
              className={`${styles.scheduleButton} ${
                schedulePopoverOpen ? styles.scheduleButtonActive : ""
              }`}
            >
              <CalendarDays size={14} />
              <span>Schedule</span>
              <span
                className={`${styles.scheduleRangeText} ${
                  schedulePopoverOpen ? styles.scheduleRangeTextActive : ""
                }`}
              >
                {formatRangeShort(rangeStart, rangeEnd)}
              </span>
              <ChevronDown
                size={13}
                style={{
                  opacity: 0.7,
                  marginLeft: 2,
                  transform: schedulePopoverOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.15s ease",
                }}
              />
            </button>
          </Popover>

          <Flex align="center" gap={8} className={styles.legendContainer}>
            <span className={styles.legendTitle}>Legend</span>
            {LEGEND_ITEMS.map((item) => (
              <span key={item.kind} className={styles.legendItem}>
                <span className={`${styles.legendDot} ${item.colorClass}`} />
                <span className={styles.legendLabel}>{item.label}</span>
              </span>
            ))}
          </Flex>
        </Flex>

        <Flex align="center" gap={8}>
          {editMode && (
            <Flex align="center" gap={6}>
              <button
                type="button"
                onClick={allCreatorsSelected ? onClearCreatorSelection : onSelectAllCreators}
                className={styles.selectAllBtn}
              >
                {allCreatorsSelected ? (
                  <CheckSquare size={14} className={styles.checkSquareIcon} />
                ) : (
                  <Square size={14} className={styles.squareIcon} />
                )}
                <span>{allCreatorsSelected ? "Clear all" : "Select all"}</span>
              </button>
              <span className={styles.selectionCounter}>
                {selectedCount}/{totalCreatorsCount}
              </span>
            </Flex>
          )}

          {editMode && onCancelEditMode && (
            <button
              type="button"
              onClick={onCancelEditMode}
              disabled={isSaving}
              className={styles.cancelEditBtn}
            >
              <X size={14} /> <span>Cancel</span>
            </button>
          )}

          <button
            type="button"
            onClick={onToggleEditMode}
            disabled={isSaving}
            className={`${styles.editDatesBtn} ${
              editMode ? styles.editDatesBtnActive : ""
            }`}
          >
            {editMode ? (
              isSaving ? (
                <>
                  <Loader2 size={14} className={styles.spin} /> <span>Saving...</span>
                </>
              ) : (
                <>
                  <Check size={14} /> <span>Done</span>
                </>
              )
            ) : (
              <>
                <Pencil size={14} /> <span>Edit dates</span>
              </>
            )}
          </button>
        </Flex>
      </Flex>

      {editMode && (
        <p className={styles.editModeHint}>
          {someCreatorsSelected
            ? "Drag any bar — all stages update for selected creators"
            : "Select creators, then drag a bar to change dates"}
        </p>
      )}
    </div>
  );
};
